// "Ajustar meses" no cronograma atual (revisão de 2026-10-07): uma linha por competência, da
// primeira à última com valor, com a referência (a origem do alvo), o ajustado editável e a
// diferença. Ações por linha: adiar para o mês seguinte e mover para outro mês. É a mesma
// programação do "Personalizado" vista por mês — as duas editam as mesmas linhas.
//
// Só apresentação: as contas são de `referenceSchedule.js`, e o editor é dono das linhas.
import { Fragment, useState } from "react";

import { competenceText } from "./format";
import { centsToReaisInput, monthsBetween, parseReaisToCents, reais } from "./monthEdit";
import { shiftMonth } from "./hierarchy";
import { WEIGHTED_MAX_OFFSET as MAX_OFFSET } from "./profile";
import {
  adjustedByMonth,
  monthRows,
  moveMonthCents,
  postponeMonth,
  setMonthCents,
} from "./referenceSchedule";

const MoveForm = ({ month, available, months, onMove, onCancel }) => {
  const [to, setTo] = useState("");
  const [amount, setAmount] = useState(() => centsToReaisInput(available));
  const cents = parseReaisToCents(amount);
  const valid = to !== "" && Number.isSafeInteger(cents) && cents > 0 && cents <= available;
  return (
    <div className="ampli-month-edit__move" role="group" aria-label={`Mover valor de ${competenceText(month)}`}>
      <label className="ampli-rule-editor__field">
        <span className="card-label">Mover para</span>
        <select
          className="default-input"
          aria-label={`Mês de destino de ${competenceText(month)}`}
          value={to}
          onChange={(event) => setTo(event.target.value)}
        >
          <option value="">Escolha o mês</option>
          {months.filter((candidate) => candidate !== month).map((candidate) => (
            <option key={candidate} value={candidate}>{competenceText(candidate)}</option>
          ))}
        </select>
      </label>
      <label className="ampli-rule-editor__field">
        <span className="card-label">Valor (R$)</span>
        <input
          type="text"
          inputMode="decimal"
          className="default-input"
          aria-label={`Valor a mover de ${competenceText(month)}`}
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
        />
      </label>
      <div className="ampli-month-edit__move-actions">
        <button type="button" className="button button-ghost small" onClick={onCancel}>Cancelar</button>
        <button type="button" className="button button-accent small" disabled={!valid} onClick={() => onMove(to, cents)}>
          Mover
        </button>
      </div>
    </div>
  );
};

const ReferenceMonthsTable = ({
  reference,
  lines,
  startMonth,
  balanceCents,
  referenceLabel = "Referência",
  editable = true,
  problem = null,
  onChangeLines,
  onBackToReference,
  backToReferenceDisabled = false,
}) => {
  const [drafts, setDrafts] = useState({});
  const [moving, setMoving] = useState(null);
  const months = monthRows(reference, lines, startMonth);
  const adjusted = adjustedByMonth(lines, startMonth);
  // Destinos do "Mover para…" e do "Adicionar mês": até o fim da faixa de defasagem aceita.
  const lastAllowed = startMonth ? shiftMonth(startMonth, MAX_OFFSET) : null;
  const destinations = months.length > 0 && lastAllowed ? monthsBetween(months[0], lastAllowed) : months;
  const lastRow = months[months.length - 1] ?? null;
  const canAddMonth = Boolean(lastRow && lastAllowed && lastRow < lastAllowed);
  const change = (next) => {
    setDrafts({});
    onChangeLines(next);
  };
  const totals = months.reduce(
    (sum, month) => ({
      reference: sum.reference + (reference.map.get(month) ?? 0),
      adjusted: sum.adjusted + (adjusted.get(month) ?? 0),
    }),
    { reference: 0, adjusted: 0 },
  );

  return (
    <div className="ampli-month-edit">
      <div className="ampli-rule-projection ampli-month-edit__table" role="region" aria-label="Ajustar meses" tabIndex={0}>
        <table>
          <caption className="visually-hidden">Ajuste mês a mês do cronograma atual</caption>
          <thead>
            <tr>
              <th scope="col">Mês</th>
              <th scope="col" className="num">{referenceLabel}</th>
              <th scope="col" className="num">Ajustado</th>
              <th scope="col" className="num">Diferença</th>
              {editable ? <th scope="col"><span className="visually-hidden">Ações</span></th> : null}
            </tr>
          </thead>
          <tbody>
            {months.map((month) => {
              const base = reference.map.get(month) ?? 0;
              const value = adjusted.get(month) ?? 0;
              const delta = value - base;
              const draft = drafts[month];
              const next = shiftMonth(month, 1);
              return (
                <Fragment key={month}>
                  <tr className={delta !== 0 ? "is-changed" : undefined} data-testid={`month-row-${month}`}>
                    <th scope="row">{competenceText(month)}</th>
                    <td className="num">{reais(base)}</td>
                    <td className="num">
                      {editable ? (
                        <input
                          type="text"
                          inputMode="decimal"
                          className="default-input ampli-month-edit__input"
                          aria-label={`Ajustado em ${competenceText(month)}`}
                          value={draft ?? centsToReaisInput(value)}
                          aria-invalid={draft !== undefined && Number.isNaN(parseReaisToCents(draft))}
                          onChange={(event) => {
                            const text = event.target.value;
                            setDrafts((current) => ({ ...current, [month]: text }));
                            const cents = parseReaisToCents(text);
                            if (Number.isSafeInteger(cents) && cents >= 0) {
                              onChangeLines(setMonthCents(lines, startMonth, month, cents));
                            }
                          }}
                          onBlur={() => setDrafts(({ [month]: _left, ...rest }) => rest)}
                        />
                      ) : reais(value)}
                    </td>
                    <td className={`num${delta > 0 ? " is-up" : delta < 0 ? " is-down" : ""}`}>
                      {delta === 0 ? "—" : `${delta > 0 ? "+" : "−"}${reais(Math.abs(delta))}`}
                    </td>
                    {editable ? (
                      <td className="ampli-month-edit__actions">
                        <button
                          type="button"
                          className="button button-ghost small"
                          disabled={value <= 0 || !lastAllowed || next > lastAllowed}
                          aria-label={`Adiar ${competenceText(month)} para o mês seguinte`}
                          onClick={() => change(postponeMonth(lines, startMonth, month))}
                        >
                          Adiar para o mês seguinte
                        </button>
                        <button
                          type="button"
                          className="button button-ghost small"
                          disabled={value <= 0}
                          aria-expanded={moving === month}
                          aria-label={`Mover valor de ${competenceText(month)} para outro mês`}
                          onClick={() => setMoving((current) => (current === month ? null : month))}
                        >
                          Mover para…
                        </button>
                      </td>
                    ) : null}
                  </tr>
                  {moving === month && editable ? (
                    <tr className="ampli-month-edit__move-row">
                      <td colSpan={5}>
                        <MoveForm
                          month={month}
                          available={value}
                          months={destinations}
                          onCancel={() => setMoving(null)}
                          onMove={(to, cents) => {
                            change(moveMonthCents(lines, startMonth, month, to, cents));
                            setMoving(null);
                          }}
                        />
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <th scope="row">Total</th>
              <td className="num">{reais(totals.reference)}</td>
              <td className="num">{reais(totals.adjusted)}</td>
              <td className="num">—</td>
              {editable ? <td /> : null}
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="ampli-month-edit__footer">
        <p
          className={`ampli-month-edit__balance${balanceCents !== 0 ? " is-open" : ""}`}
          role="status"
          data-testid="months-balance"
        >
          {balanceCents === 0
            ? "Saldo a redistribuir: R$ 0,00"
            : balanceCents < 0
              ? `Saldo a redistribuir: ${reais(-balanceCents)}; coloque em algum mês`
              : `Os meses passam do valor do alvo em ${reais(balanceCents)}; tire de algum mês`}
        </p>
        {editable ? (
          <div className="ampli-month-edit__tools">
            <button
              type="button"
              className="button button-ghost small"
              disabled={!canAddMonth}
              onClick={() => change(setMonthCents(lines, startMonth, shiftMonth(lastRow, 1), 0))}
            >
              Adicionar mês
            </button>
            <button
              type="button"
              className="button button-ghost small"
              disabled={backToReferenceDisabled}
              onClick={() => {
                setDrafts({});
                setMoving(null);
                onBackToReference();
              }}
            >
              Voltar à referência
            </button>
          </div>
        ) : null}
      </div>
      {problem ? <p className="alert alert-error" role="alert">{problem}</p> : null}
    </div>
  );
};

export default ReferenceMonthsTable;
